// src/components/Popups/StudentInfoPopup.jsx
import { createPortal } from "react-dom";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchUserReservations } from "../../../store/reservationsSlice";
import { updateStudent, removeStudent } from "../../../store/studentsSlice";
import { fetchGroups } from "../../../store/groupsSlice";

import { getInstructors } from "../../../api/instructorsService";
import { deleteAllReservationsForUser } from "../../../api/reservationsService";
import { getUserById } from "../../../api/usersService";
import { isInstructorSelfProfileMode } from "../../../constants/instructors";
import {
   isViewerInstructorPerson,
   shouldMaskOtherInstructorsInStudentProfile,
} from "../../../auth/studentProfileAccess";
import { useResolvedInstructor } from "../../../hooks/useResolvedInstructor";
import { ROLES, hasAnyRole } from "../../../auth/access";
import cancelIcon from "../../../assets/svg/cancel.svg";

import {
   getExamHistoryForStudentIdAll,
   downloadExamPdf,
} from "../../../api/examService";
import { downloadPracticalExamIndividualDocument } from "../../../api/practicalExamSlotsService";
import { getAdminRatingsAll } from "../../../api/instructorRatingsService";

import {
   closePopup as closePopupUI,
   openSubPopup,
} from "../../Common/popupUIStore";
import UserAvatar from "../../Common/UserAvatar";
import ProfilePhotoAvatar from "../StaffProfile/ProfilePhotoAvatar";
import { ownAvatarUrl, rememberAvatarChange } from "../../../utils/userAvatar";
import AlertPills from "../../Utils/AlertPills";
import UIIcon from "../../Common/UIIcon";
import { pressPoint } from "../../UI/glass";
import { StudentInfoContext } from "./StudentInfoContext";
import StudentOverviewTab from "./StudentOverviewTab";
import StudentReservationsTab from "./StudentReservationsTab";
import StudentExamsTab from "./StudentExamsTab";
import StudentReviewsTab from "./StudentReviewsTab";
import StudentFinanceTab from "./StudentFinanceTab";
import StudentChatSubPopup from "./StudentChatSubPopup";
import ProfileTabs from "../../Common/ProfileTabs";
import PopupActions from "../../Common/PopupActions";
import { ButtonUI } from "../../UI";
import reservationsIcon from "../../../assets/svg/mdi--calendar-outline.svg";
import profileIcon from "../../../assets/svg/acc.svg";
import examIcon from "../../../assets/svg/mdi--book-clock-outline.svg";
import reviewsIcon from "../../../assets/svg/material-symbols--star-outline-rounded.svg";
import messagesIcon from "../../../assets/svg/tabler--message.svg";
import { formatLei, getUserBalance } from "../../../api/balanceService";
import useSocketEvent from "../../../hooks/useSocketEvent";
import financeIcon from "../../../assets/svg/mdi--cash-multiple.svg";
import {
   clampInt,
   resolveStudentId,
   getInitials,
   normExtras,
   buildFormDataFromStudent,
   getAvatarColorFromName,
   getAvatarColorFromKey,
   mergeReservationRecords,
   getReservationStartMs,
   getReservationStudentId,
   getReservationPrimaryId,
   getReservationLessonKey,
   getReservationRowKey,
   getReservationMergeKey,
   getReservationSlotSignature,
   shouldPreferIncomingReservation,
   isReservationCancelled,
   isCurrentReservation,
   normalizeReservationId,
   readReservationField,
   isTemporaryReservationId,
   readBooleanField,
   hasCanBookPracticalExamField,
   isLikelyCssColor,
   fmtReservationDate,
   fmtReservationTime,
   readReservationInstructorName,
   readReservationSector,
   readReservationGearbox,
   readReservationColor,
   isReservationFavorite,
   isReservationImportant,
   translateAttemptStatus,
   fmtIsoDDMMYYYY_HHMM,
} from "./studentInfoShared";
export default function StudentInfoPopup({
   student,
   onClose,
   studentProfileMode = "",
   fromReservationId = "",
   fromReservationStartISO = "",
   fromReservationStart = "",
}) {
   const dispatch = useDispatch();
   const studentId = useMemo(() => resolveStudentId(student), [student]);
   const normalizedStudentProfileMode = String(studentProfileMode || "")
      .trim()
      .toLowerCase();
   const isSuperiorInstructorView = isInstructorSelfProfileMode(
      normalizedStudentProfileMode,
   );
   const canEditStudentProfile = !isSuperiorInstructorView;
   const canOpenReservationEdit = !isSuperiorInstructorView;
   const canSeeExamTab = !isSuperiorInstructorView;
   const canSeeReviewsTab = !isSuperiorInstructorView;
   const canShowAdminNote = !isSuperiorInstructorView;
   const canShowSensitiveProfileFields = !isSuperiorInstructorView;
   const showIdnpInHeaderMeta = !isSuperiorInstructorView;

   // Un instructor (în afară de SUPERIOR) nu vede cu ce alți instructori a
   // lucrat elevul: numele lor, înlocuirile și instructorul preferat se
   // maschează; rămâne vizibil doar propriul nume.
   const { instructor: viewerInstructor, user: viewerUser } =
      useResolvedInstructor();
   // Backend-ul permite ștergerea contului doar adminului și managerului.
   const isStaffViewer = hasAnyRole(viewerUser, [ROLES.ADMIN, ROLES.MANAGER]);
   const canDeleteStudent = isStaffViewer;
   // Chatul cu elevul și finanțele sunt doar pentru admin / manager.
   const canSeeChatTab = isStaffViewer && !isSuperiorInstructorView;
   const canSeeFinanceTab = isStaffViewer && !isSuperiorInstructorView;
   const maskOtherInstructors = shouldMaskOtherInstructorsInStudentProfile(
      viewerUser,
      viewerInstructor,
   );

   const storeStudent = useSelector((s) => {
      if (!studentId) return null;
      const list = s.students?.list || [];
      return (
         list.find((u) => String(resolveStudentId(u)) === String(studentId)) ||
         null
      );
   });

   const {
      list: reservationsList = [],
      loading: loadingGlobal = false,
      error: errorGlobal = null,
      byStudent = {},
      loadingByStudent = {},
      errorByStudent = {},
   } = useSelector((s) => s.reservations || {});

   const studentReservations = useMemo(() => {
      if (!studentId) return [];
      const list = byStudent?.[studentId];
      return Array.isArray(list) ? list : [];
   }, [byStudent, studentId]);

   const loading = studentId
      ? Boolean(loadingByStudent?.[studentId])
      : Boolean(loadingGlobal);
   const error = studentId ? errorByStudent?.[studentId] || null : errorGlobal;

   // edit form: DOAR date profil
   const [formData, setFormData] = useState({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      idnp: "",
      groupId: "",
   });

   // local snapshot
   const [liveStudent, setLiveStudent] = useState(student || {});
   // cont de oaspete: nu intră în platformă, deci nici chat
   const isGuestStudent = liveStudent?.isGuest === true || student?.isGuest === true;

   // save profile
   const [saving, setSaving] = useState(false);
   const [saveError, setSaveError] = useState("");
   const [deleteError, setDeleteError] = useState("");
   const [deleting, setDeleting] = useState(false);

   // tabs
   const [tab, setTab] = useState("reservations");
   const [attempts, setAttempts] = useState([]);
   const [attemptsLoading, setAttemptsLoading] = useState(false);
   const [attemptsError, setAttemptsError] = useState("");
   const [reviews, setReviews] = useState([]);
   const [reviewsLoading, setReviewsLoading] = useState(false);
   const [reviewsError, setReviewsError] = useState("");
   const [reviewsAscending, setReviewsAscending] = useState(false);
   const [reservationsAscending, setReservationsAscending] = useState(true);
   const [profileDirty, setProfileDirty] = useState(false);

   const {
      list: availableGroups = [],
      status: groupsStatus = "idle",
      error: groupsError = null,
   } = useSelector((s) => s.groups || {});

   const shouldLoadGroups = canShowSensitiveProfileFields && tab === "profile";

   // pdf download
   const [downloadingId, setDownloadingId] = useState(null);
   const [downloadError, setDownloadError] = useState("");
   const [downloadingDocumentId, setDownloadingDocumentId] = useState(null);

   /* ===================== note (autosave on blur) ===================== */

   const [noteValue, setNoteValue] = useState("");
   const [noteSaving, setNoteSaving] = useState(false);
   const [noteError, setNoteError] = useState("");

   const noteSaveSeqRef = useRef(Promise.resolve());
   const noteDesiredRef = useRef("");
   const noteLastSavedRef = useRef("");

   const requestSaveNote = (nextText) => {
      if (!studentId) return;

      const desired = String(nextText ?? "");
      noteDesiredRef.current = desired;

      setNoteSaving(true);
      setNoteError("");

      noteSaveSeqRef.current = noteSaveSeqRef.current
         .then(async () => {
            const cur = noteDesiredRef.current;
            if (cur === noteLastSavedRef.current) return;

            const updated = await dispatch(
               updateStudent({
                  id: studentId,
                  data: { privateMessage: cur },
               }),
            ).unwrap();

            const serverMsg = String(updated?.privateMessage ?? cur);
            noteLastSavedRef.current = serverMsg;

            setNoteValue(serverMsg);
            setLiveStudent((p) => ({ ...p, privateMessage: serverMsg }));
         })
         .catch((e) => {
            setNoteError("Nu s-a putut salva notița. Vezi consola / Network.");
         })
         .finally(() => {
            if (noteDesiredRef.current === noteLastSavedRef.current)
               setNoteSaving(false);
         });
   };

   const onNoteChange = (e) => {
      setNoteError("");
      setNoteValue(e.target.value);
   };

   // notița se scrie în rândul elevului: un clic pe el (sau pe creion) deschide locul de scris
   const [noteEditing, setNoteEditing] = useState(false);
   const onNoteBlur = () => {
      setNoteEditing(false);
      requestSaveNote(noteValue);
   };

   // Enter = save (Shift+Enter = newline); Escape = gata, tot cu salvare
   const onNoteKeyDown = (e) => {
      if ((e.key === "Enter" && !e.shiftKey) || e.key === "Escape") {
         e.preventDefault();
         e.stopPropagation();
         e.currentTarget.blur();
      }
   };

   /* ===================== extras (Doc / Individual / Absente) ===================== */

   const [extrasSaving, setExtrasSaving] = useState(false);
   const [extrasError, setExtrasError] = useState("");
   const [, setExtrasBase] = useState(() => normExtras(student));
   const [extrasForm, setExtrasForm] = useState(() => normExtras(student));

   // absences text input (UI)
   const [absText, setAbsText] = useState("0");
   const absFocusedRef = useRef(false);

   // autosave queue (ca în PP)
   const saveSeqRef = useRef(Promise.resolve());
   const desiredRef = useRef(normExtras(student));
   const lastSentRef = useRef("");

   // Valorile reale de pe server (GET /users/:id): { studentId, extras }.
   // Listele din care se deschide fișa (calendarul, „Elevi”) nu au aceste
   // câmpuri — până nu vine elevul de pe server, comutatoarele sunt blocate și
   // nu se salvează nimic, ca o valoare necunoscută să nu le rescrie.
   const serverExtrasRef = useRef(null);
   const [extrasReady, setExtrasReady] = useState(false);

   const requestSaveExtras = (nextForm) => {
      if (!studentId) return;

      const desired = normExtras(nextForm);
      desiredRef.current = desired;

      setExtrasSaving(true);
      setExtrasError("");

      saveSeqRef.current = saveSeqRef.current
         .then(async () => {
            const cur = desiredRef.current;
            const curJson = JSON.stringify(cur);

            if (curJson === lastSentRef.current) return;

            // se trimite doar ce diferă de server: un câmp pe care fișa nu-l
            // știe sigur nu mai rescrie valoarea reală a elevului
            const server =
               serverExtrasRef.current?.studentId === String(studentId)
                  ? serverExtrasRef.current.extras
                  : null;
            if (!server) return;

            const payload = {};
            for (const key of [
               "medical_documents",
               "individual_work",
               "number_of_absences",
               "fromOtherSchool",
            ]) {
               if (cur[key] !== server[key]) payload[key] = cur[key];
            }
            if (
               cur.__hasCanBookPracticalExam &&
               cur.canBookPracticalExam !== server.canBookPracticalExam
            ) {
               payload.canBookPracticalExam = cur.canBookPracticalExam;
            }
            if (!Object.keys(payload).length) {
               lastSentRef.current = curJson;
               return;
            }

            const updated = await dispatch(
               updateStudent({ id: studentId, data: payload }),
            ).unwrap();
            lastSentRef.current = curJson;

            // mismatch detection (dacă backend ignoră)
            const mismatches = [];
            if (
               "medical_documents" in payload &&
               typeof updated?.medical_documents === "boolean" &&
               updated.medical_documents !== cur.medical_documents
            )
               mismatches.push("medical_documents");
            if (
               "individual_work" in payload &&
               typeof updated?.individual_work === "boolean" &&
               updated.individual_work !== cur.individual_work
            )
               mismatches.push("individual_work");
            if (
               "number_of_absences" in payload &&
               updated?.number_of_absences != null &&
               Number(updated.number_of_absences) !== cur.number_of_absences
            )
               mismatches.push("number_of_absences");
            if ("canBookPracticalExam" in payload) {
               const serverCanBook = readBooleanField(updated, [
                  "canBookPracticalExam",
                  "can_book_practical_exam",
               ]);
               if (
                  !hasCanBookPracticalExamField(updated) ||
                  serverCanBook !== cur.canBookPracticalExam
               ) {
                  mismatches.push("canBookPracticalExam");
               }
            }

            const serverState = normExtras({
               ...server,
               ...payload,
               ...(updated || {}),
            });
            serverExtrasRef.current = {
               studentId: String(studentId),
               extras: serverState,
            };
            const liveExtras = {
               medical_documents: serverState.medical_documents,
               individual_work: serverState.individual_work,
               number_of_absences: serverState.number_of_absences,
            };

            if (serverState.__hasCanBookPracticalExam) {
               liveExtras.canBookPracticalExam =
                  serverState.canBookPracticalExam;
            }

            if (mismatches.length) {
               setExtrasError(
                  `Backend a ignorat: ${mismatches.join(", ")}. Payload-ul e corect, dar serverul nu aplică aceste câmpuri.`,
               );
            }

            setExtrasBase(serverState);

            // nu suprascriem dacă user a schimbat iar între timp
            const desiredNowJson = JSON.stringify(desiredRef.current);
            if (desiredNowJson === curJson) setExtrasForm(serverState);

            // sync liveStudent
            setLiveStudent((p) => ({
               ...p,
               ...payload,
               ...(updated || {}),
               ...liveExtras,
            }));
         })
         .catch((e) => {
            setExtrasError("Salvarea a eșuat. Vezi consola / Network.");
         })
         .finally(() => {
            const desiredNowJson = JSON.stringify(desiredRef.current);
            if (desiredNowJson === lastSentRef.current) setExtrasSaving(false);
         });
   };

   const toggleField = (key) => {
      setExtrasError("");
      setExtrasForm((p) => {
         const next = {
            ...p,
            [key]: !Boolean(p[key]),
            ...(key === "canBookPracticalExam"
               ? { __hasCanBookPracticalExam: true }
               : {}),
         };
         requestSaveExtras(next);
         return next;
      });
   };

   const onAbsFocus = (e) => {
      absFocusedRef.current = true;
      const el = e.currentTarget;
      const n = clampInt(extrasForm.number_of_absences ?? 0, 0);

      setAbsText(n === 0 ? "" : String(n));

      requestAnimationFrame(() => {
         if (el && typeof el.select === "function") el.select();
      });
   };

   const onAbsChange = (e) => {
      const digits = String(e.target.value ?? "").replace(/[^\d]/g, "");
      const cleaned = digits.replace(/^0+(?=\d)/, "");
      setAbsText(cleaned);

      const n = cleaned === "" ? 0 : clampInt(parseInt(cleaned, 10), 0);
      setExtrasForm((p) => ({ ...p, number_of_absences: n }));
   };

   const onAbsBlur = () => {
      absFocusedRef.current = false;

      const digits = String(absText ?? "").replace(/[^\d]/g, "");
      const cleaned = digits.replace(/^0+(?=\d)/, "");
      const n = cleaned === "" ? 0 : clampInt(parseInt(cleaned, 10), 0);

      setAbsText(String(n));

      setExtrasForm((p) => {
         const next = { ...p, number_of_absences: n };
         requestSaveExtras(next);
         return next;
      });
   };

   const onAbsKeyDown = (e) => {
      if (e.key === "Enter") {
         e.preventDefault();
         e.currentTarget.blur();
      }
   };

   // keep absText synced when extrasForm changes (dacă nu e focus)
   useEffect(() => {
      if (absFocusedRef.current) return;
      setAbsText(String(clampInt(extrasForm.number_of_absences ?? 0, 0)));
   }, [extrasForm.number_of_absences]);

   /* ===================== close ===================== */

   const safeClose = () => {
      if (typeof onClose === "function") onClose();
      else {
         try {
            closePopupUI();
         } catch {}
      }
   };

   /* ===================== init/reset when student changes ===================== */

   useEffect(() => {
      setLiveStudent(student || {});
      setTab("reservations");
      setReservationsAscending(true);
      setReviews([]);
      setReviewsError("");
      setReviewsLoading(false);
      setReviewsAscending(false);
      setSaveError("");
      setSaving(false);
      setFormData(buildFormDataFromStudent(student));
      setProfileDirty(false);

      // note init + reset queue
      const baseNote = String(student?.privateMessage || "");
      setNoteValue(baseNote);

      noteSaveSeqRef.current = Promise.resolve();
      noteDesiredRef.current = baseNote;
      noteLastSavedRef.current = baseNote;
      setNoteSaving(false);
      setNoteError("");

      // extras init + reset queue (valorile de pe server au întâietate:
      // obiectul primit de la listă poate să nu aibă câmpurile)
      const known =
         serverExtrasRef.current?.studentId === String(studentId)
            ? serverExtrasRef.current.extras
            : null;
      const base = known || normExtras(student);
      setExtrasBase(base);
      setExtrasForm(base);
      setAbsText(String(base.number_of_absences ?? 0));
      absFocusedRef.current = false;

      saveSeqRef.current = Promise.resolve();
      desiredRef.current = base;
      lastSentRef.current = known ? JSON.stringify(base) : "";
      setExtrasSaving(false);
      setExtrasError("");
   }, [student]); // eslint-disable-line react-hooks/exhaustive-deps

   // Doc. medicale / Lucru individual / Ex. practic / Absențe: valorile reale
   // ale elevului, de pe server; până atunci comutatoarele sunt blocate.
   useEffect(() => {
      serverExtrasRef.current = null;
      setExtrasReady(false);
      if (!studentId) return undefined;

      let alive = true;
      getUserById(studentId)
         .then((fresh) => {
            if (!alive || !fresh) return;
            const base = normExtras(fresh);
            serverExtrasRef.current = { studentId: String(studentId), extras: base };
            desiredRef.current = base;
            lastSentRef.current = JSON.stringify(base);
            setExtrasBase(base);
            setExtrasForm(base);
            if (!absFocusedRef.current) {
               setAbsText(String(base.number_of_absences ?? 0));
            }
            setLiveStudent((p) => ({
               ...p,
               medical_documents: base.medical_documents,
               individual_work: base.individual_work,
               number_of_absences: base.number_of_absences,
               canBookPracticalExam: base.canBookPracticalExam,
               isGuest: fresh.isGuest === true,
            }));
            // notița de pe server, dacă rândul primit n-o avea (ex. deschis din Mesaje) și nu se scrie acum
            const freshNote = fresh?.privateMessage != null ? String(fresh.privateMessage) : null;
            if (freshNote != null && noteDesiredRef.current === noteLastSavedRef.current && freshNote !== noteLastSavedRef.current) {
               noteDesiredRef.current = freshNote;
               noteLastSavedRef.current = freshNote;
               setNoteValue(freshNote);
            }
            setExtrasReady(true);
         })
         .catch(() => {
            if (alive) {
               setExtrasError(
                  "Nu am putut încărca datele elevului. Comutatoarele rămân blocate — redeschide fișa.",
               );
            }
         });

      return () => {
         alive = false;
      };
   }, [studentId]);

   // keep in sync with store updates (ex: privateMessage saved elsewhere)
   useEffect(() => {
      if (!storeStudent?.id) return;

      setLiveStudent((p) => ({ ...p, ...storeStudent }));

      if (!profileDirty) {
         setFormData(buildFormDataFromStudent(storeStudent));

         const baseNote = String(storeStudent?.privateMessage || "");
         setNoteValue(baseNote);
         noteDesiredRef.current = baseNote;
         noteLastSavedRef.current = baseNote;
         setNoteSaving(false);
         setNoteError("");
         // comutatoarele NU se iau din listă (GET /users nu are aceste
         // câmpuri și le-ar arăta stinse); vin de pe server, mai sus
      }
   }, [storeStudent, profileDirty]);

   /* ===================== reservations load ===================== */

   useEffect(() => {
      if (studentId) dispatch(fetchUserReservations(String(studentId)));
   }, [dispatch, studentId]);

   useEffect(() => {
      if (!shouldLoadGroups) return;
      if (groupsStatus !== "idle") return;
      dispatch(fetchGroups());
   }, [dispatch, groupsStatus, shouldLoadGroups]);

   useEffect(() => {
      if ((canSeeExamTab && !isGuestStudent) || tab !== "attempts") return;
      setTab("reservations");
   }, [canSeeExamTab, isGuestStudent, tab]);

   useEffect(() => {
      if ((canSeeReviewsTab && !isGuestStudent) || tab !== "reviews") return;
      setTab("reservations");
   }, [canSeeReviewsTab, isGuestStudent, tab]);

   useEffect(() => {
      if (
         (tab === "messages" && (!canSeeChatTab || isGuestStudent)) ||
         (tab === "finance" && (!canSeeFinanceTab || isGuestStudent))
      ) {
         setTab("reservations");
      }
   }, [canSeeChatTab, canSeeFinanceTab, isGuestStudent, tab]);

   // oaspetele nu intră în platformă și n-are balanță: fără Examen, Recenzii, Mesaje și Finanțe
   const profileTabs = useMemo(
      () => [
         { key: "reservations", label: "Programări", icon: reservationsIcon },
         { key: "profile", label: "Profil", icon: profileIcon },
         canSeeExamTab && !isGuestStudent && { key: "attempts", label: "Examen", icon: examIcon },
         canSeeReviewsTab &&
            !isGuestStudent && {
               key: "reviews",
               label: "Recenzii",
               icon: reviewsIcon,
            },
         canSeeChatTab &&
            !isGuestStudent && { key: "messages", label: "Mesaje", icon: messagesIcon },
         canSeeFinanceTab && !isGuestStudent && { key: "finance", label: "Finanțe", icon: financeIcon },
      ],
      [canSeeExamTab, canSeeReviewsTab, canSeeChatTab, canSeeFinanceTab, isGuestStudent],
   );

   // soldul elevului, lângă numele lui (se reîmprospătează la orice schimbare: balance:update)
   const [balanceBani, setBalanceBani] = useState(null);
   const loadBalance = useCallback(async () => {
      if (!canSeeFinanceTab || !studentId || isGuestStudent) return;
      try {
         const res = await getUserBalance(studentId, { limit: 1 });
         setBalanceBani(Number(res?.balanceBani) || 0);
      } catch {
         setBalanceBani(null);
      }
   }, [canSeeFinanceTab, studentId, isGuestStudent]);
   useEffect(() => {
      setBalanceBani(null);
      loadBalance();
   }, [loadBalance]);
   useSocketEvent(
      "balance:update",
      (payload) => {
         if (Number(payload?.userId) === Number(studentId)) loadBalance();
      },
      !!studentId && canSeeFinanceTab,
   );

   /* ===================== profile edit handlers ===================== */

   const handleChange = (e) => {
      const { name, value } = e.target;
      setProfileDirty(true);
      setFormData((p) => ({ ...p, [name]: value }));
   };

   const resetProfileForm = () => {
      setSaveError("");
      setFormData(buildFormDataFromStudent({ ...student, ...liveStudent }));
      setProfileDirty(false);
   };

   const handleSave = async () => {
      if (!canEditStudentProfile) return;
      if (!studentId) return;

      setSaving(true);
      setSaveError("");

      try {
         const rawGroupId = String(formData.groupId || "").trim();
         const savedGroupId =
            liveStudent?.groupId ??
            student?.groupId ??
            storeStudent?.groupId ??
            null;

         const payload = {
            firstName: String(formData.firstName || "").trim(),
            lastName: String(formData.lastName || "").trim(),
            email: String(formData.email || "").trim(),
            phone: String(formData.phone || "").trim(),
            idnp: String(formData.idnp || "").trim(),
         };

         if (rawGroupId) {
            const nextGroupId = Number(rawGroupId);

            if (!Number.isInteger(nextGroupId) || nextGroupId <= 0) {
               throw new Error("Grupa selectată nu este validă.");
            }

            if (String(nextGroupId) !== String(savedGroupId ?? "")) {
               payload.groupId = nextGroupId;
            }
         }

         const updated = await dispatch(
            updateStudent({ id: studentId, data: payload }),
         ).unwrap();

         if (typeof payload.groupId !== "undefined") {
            await dispatch(fetchGroups());
         }

         const next = { ...liveStudent, ...payload, ...(updated || {}) };
         setLiveStudent(next);
         setFormData(buildFormDataFromStudent(next));
         setProfileDirty(false);
      } catch (err) {
         setSaveError(
            err?.message || "Actualizarea a eșuat. Vezi consola / Network.",
         );
      } finally {
         setSaving(false);
      }
   };

   /* ===================== delete ===================== */

   // Ștergere logică a contului elevului (DELETE /users/{id}): dispare din
   // liste, nu se mai poate conecta, istoricul rămâne. Backend-ul refuză cu
   // mesaj clar când rolul nu are voie; îl arătăm în profil, nu în alert.
   const handleDelete = async () => {
      if (!studentId || deleting) return;
      setDeleting(true);
      setDeleteError("");
      try {
         await dispatch(removeStudent(studentId)).unwrap();
         setTab("reservations");
         setLiveStudent({});
         safeClose();
      } catch (err) {
         setDeleteError(err?.message || "Ștergerea a eșuat.");
      } finally {
         setDeleting(false);
      }
   };

   const [deletingAllReservations, setDeletingAllReservations] = useState(false);
   const [deleteAllReservationsError, setDeleteAllReservationsError] =
      useState("");
   const [purgeOpen, setPurgeOpen] = useState(false);
   const [purgeChecked, setPurgeChecked] = useState(false);

   const resetPurgeUi = () => {
      setPurgeOpen(false);
      setPurgeChecked(false);
      setDeleteAllReservationsError("");
   };

   const handleDeleteAllReservations = async () => {
      if (!studentId || !canEditStudentProfile) return false;
      setDeletingAllReservations(true);
      setDeleteAllReservationsError("");
      try {
         await deleteAllReservationsForUser(studentId);
         await dispatch(fetchUserReservations(String(studentId))).unwrap();
         resetPurgeUi();
         return true;
      } catch (err) {
         setDeleteAllReservationsError(
            err?.message || "Nu am putut șterge toate rezervările.",
         );
         return false;
      } finally {
         setDeletingAllReservations(false);
      }
   };

   /* ===================== reservations derived ===================== */

   const myReservations = useMemo(() => {
      if (!studentId) return [];
      const sid = String(studentId);
      const source = [
         ...(Array.isArray(studentReservations) ? studentReservations : []),
         ...(Array.isArray(reservationsList) ? reservationsList : []),
      ];
      if (!source.length) return [];

      const mergedByKey = new Map();
      for (let index = 0; index < source.length; index++) {
         const row = source[index];
         if (String(getReservationStudentId(row) ?? "") !== sid) continue;

         const key = getReservationMergeKey(row, `merged-${index}`);
         if (!key) continue;

         const prev = mergedByKey.get(key) || null;
         if (!prev) {
            mergedByKey.set(key, row);
            continue;
         }

         const merged = shouldPreferIncomingReservation(prev, row)
            ? mergeReservationRecords(prev, row)
            : mergeReservationRecords(row, prev);
         mergedByKey.set(key, merged);
      }
      const primaryRows = Array.from(mergedByKey.values());
      if (primaryRows.length <= 1) return primaryRows;

      const mergedBySlot = new Map();
      for (let index = 0; index < primaryRows.length; index++) {
         const row = primaryRows[index];
         const slotSig = getReservationSlotSignature(row);
         const slotKey = slotSig ? `slot:${slotSig}` : `slot-fallback:${index}`;

         const prev = mergedBySlot.get(slotKey) || null;
         if (!prev) {
            mergedBySlot.set(slotKey, row);
            continue;
         }

         const prevId = normalizeReservationId(
            readReservationField(prev, "id", "_id", "reservationId", "reservation_id"),
         );
         const rowId = normalizeReservationId(
            readReservationField(row, "id", "_id", "reservationId", "reservation_id"),
         );
         const prevIsReal = prevId && !isTemporaryReservationId(prevId);
         const rowIsReal = rowId && !isTemporaryReservationId(rowId);

         if (prevIsReal && rowIsReal && prevId !== rowId) {
            const uniqueSlotKey = `slot-dup:${slotSig}:${rowId}`;
            mergedBySlot.set(uniqueSlotKey, row);
            continue;
         }

         const merged = shouldPreferIncomingReservation(prev, row)
            ? mergeReservationRecords(prev, row)
            : mergeReservationRecords(row, prev);
         mergedBySlot.set(slotKey, merged);
      }

      return Array.from(mergedBySlot.values());
   }, [reservationsList, studentId, studentReservations]);

   const sortedReservations = useMemo(() => {
      const arr = [...myReservations];

      arr.sort((a, b) => {
         const diff = getReservationStartMs(a) - getReservationStartMs(b);
         return reservationsAscending ? diff : -diff;
      });

      return arr;
   }, [myReservations, reservationsAscending]);

   const canPurgeReservations =
      canEditStudentProfile && sortedReservations.length > 0;

   useEffect(() => {
      if (!canPurgeReservations && purgeOpen) resetPurgeUi();
   }, [canPurgeReservations, purgeOpen]);

   const onPurgeConfirmClick = async () => {
      if (deletingAllReservations || !purgeChecked) return;
      const count = sortedReservations.length;
      const ok = window.confirm(
         `Sigur vrei să ștergi toate cele ${count} rezervări ale acestui elev? Acțiunea nu poate fi anulată ușor.`,
      );
      if (!ok) return;
      await handleDeleteAllReservations();
   };

   const reservationNumberByKey = useMemo(() => {
      const activeReservations = [...myReservations].filter(
         (reservation) => !isReservationCancelled(reservation),
      );

      activeReservations.sort((a, b) => {
         const diff = getReservationStartMs(a) - getReservationStartMs(b);
         if (diff !== 0) return diff;

         return getReservationLessonKey(a).localeCompare(
            getReservationLessonKey(b),
         );
      });

      const numbers = new Map();
      activeReservations.forEach((reservation, index) => {
         const key = getReservationLessonKey(reservation);
         if (key) numbers.set(key, index + 1);
      });

      return numbers;
   }, [myReservations]);

   const currentReservationStart =
      fromReservationStartISO || fromReservationStart || "";

   /* ===================== exam attempts ===================== */

   useEffect(() => {
      let stop = false;
      if (tab !== "attempts" || !studentId) return;

      (async () => {
         setAttemptsLoading(true);
         setAttemptsError("");
         try {
            const all = await getExamHistoryForStudentIdAll(String(studentId), {
               pageSize: 50,
               maxPages: 10,
            });
            if (!stop) setAttempts(Array.isArray(all) ? all : []);
         } catch (e) {
            if (!stop)
               setAttemptsError(
                  e?.message || "Nu am putut încărca încercările.",
               );
         } finally {
            if (!stop) setAttemptsLoading(false);
         }
      })();

      return () => {
         stop = true;
      };
   }, [tab, studentId]);

   /* ===================== reviews (recenzii lasate de elev) ===================== */

   useEffect(() => {
      let stop = false;
      if (tab !== "reviews" || !studentId || !canSeeReviewsTab) return;

      (async () => {
         setReviewsLoading(true);
         setReviewsError("");
         try {
            const rows = await getAdminRatingsAll();
            if (stop) return;

            const sid = String(studentId);
            const mine = (Array.isArray(rows) ? rows : []).filter((row) => {
               const reviewerId =
                  row?.userId ??
                  row?.user_id ??
                  row?.user?.id ??
                  row?.studentId ??
                  row?.student?.id;
               return reviewerId != null && String(reviewerId) === sid;
            });

            setReviews(mine);
         } catch (e) {
            if (!stop)
               setReviewsError(
                  e?.message || "Nu am putut încărca recenziile.",
               );
         } finally {
            if (!stop) setReviewsLoading(false);
         }
      })();

      return () => {
         stop = true;
      };
   }, [tab, studentId, canSeeReviewsTab]);

   /* ===================== derived “TOP UI” ===================== */

   const fn = String(liveStudent?.firstName ?? student?.firstName ?? "");
   const ln = String(liveStudent?.lastName ?? student?.lastName ?? "");
   // poza din antet: contul elevului (id) și poza schimbată acum (undefined = cum vine de la server)
   const [photoOverride, setPhotoOverride] = useState(undefined);
   const headStudentId = Number(resolveStudentId(liveStudent || student)) || null;
   const headPerson = {
      ...student,
      ...liveStudent,
      firstName: fn,
      lastName: ln,
      isGuest: isGuestStudent,
      ...(photoOverride !== undefined ? { avatarUrl: photoOverride, avatarFile: null } : {}),
   };
   const email = String(liveStudent?.email ?? student?.email ?? "");
   const phone = String(liveStudent?.phone ?? student?.phone ?? "");
   const idnp = String(liveStudent?.idnp ?? student?.idnp ?? "").trim();

   const rawExplicitColor = String(
      liveStudent?.color ?? student?.color ?? "",
   ).trim();
   const explicitColor = isLikelyCssColor(rawExplicitColor)
      ? rawExplicitColor
      : "";

   const desiredInstructorId =
      liveStudent?.desiredInstructorId ??
      liveStudent?.desiredInstructor?.id ??
      student?.desiredInstructorId ??
      student?.desiredInstructor?.id ??
      null;

   // ===== instructor name resolve (ID -> nume prenume) =====
   const [instructorsById, setInstructorsById] = useState(() => new Map());
   const [instLoading, setInstLoading] = useState(false);
   const [instError, setInstError] = useState("");

   const instFetchSeqRef = useRef(Promise.resolve());
   const instLoadedRef = useRef(false);

   useEffect(() => {
      let stop = false;

      if (!desiredInstructorId) return;

      const obj =
         liveStudent?.desiredInstructor ?? student?.desiredInstructor ?? null;
      const objName = obj
         ? `${obj?.firstName || ""} ${obj?.lastName || ""}`.trim()
         : "";
      if (objName) return;

      if (instLoadedRef.current && instructorsById.size) return;

      setInstLoading(true);
      setInstError("");

      instFetchSeqRef.current = instFetchSeqRef.current
         .then(async () => {
            const list = await getInstructors();
            if (stop) return;

            const map = new Map();
            for (const it of Array.isArray(list) ? list : []) {
               const id = it?.id ?? it?._id;
               if (id != null) map.set(String(id), it);
            }

            instLoadedRef.current = true;
            setInstructorsById(map);
         })
         .catch((e) => {
            if (!stop)
               setInstError("Nu am putut încărca lista de instructori.");
         })
         .finally(() => {
            if (!stop) setInstLoading(false);
         });

      return () => {
         stop = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [desiredInstructorId]);

   const desiredInstructorName = useMemo(() => {
      if (!desiredInstructorId) return "";

      const obj =
         liveStudent?.desiredInstructor ?? student?.desiredInstructor ?? null;
      const objName = obj
         ? `${obj?.firstName || ""} ${obj?.lastName || ""}`.trim()
         : "";
      if (objName) return objName;

      const hit = instructorsById.get(String(desiredInstructorId));
      const hitName = hit
         ? `${hit?.firstName || ""} ${hit?.lastName || ""}`.trim()
         : "";
      if (hitName) return hitName;

      return instLoading ? "Se încarcă..." : "—";
   }, [
      desiredInstructorId,
      liveStudent,
      student,
      instructorsById,
      instLoading,
   ]);

   const avatarKey =
      studentId ?? liveStudent?.id ?? phone ?? email ?? "__student__";
   const avatarSeed = useMemo(() => {
      const k = String(avatarKey ?? "").trim();
      if (k) return k;

      const n = `${fn} ${ln}`.trim();
      if (n) return n;

      const p = String(phone ?? "").trim();
      if (p) return p;

      const e = String(email ?? "").trim();
      if (e) return e;

      return "__student__";
   }, [avatarKey, fn, ln, phone, email]);

   const avatarInitials = useMemo(
      () => getInitials({ firstName: fn, lastName: ln }),
      [fn, ln],
   );

   const avatarBg = useMemo(() => {
      if (explicitColor) return explicitColor;

      const byName = getAvatarColorFromName({ firstName: fn, lastName: ln });
      if (byName) return byName;

      const byKey = getAvatarColorFromKey(avatarSeed);
      return byKey || "var(--black-s)";
   }, [explicitColor, fn, ln, avatarSeed]);

   const currentGroupId =
      liveStudent?.groupId ?? student?.groupId ?? storeStudent?.groupId ?? null;

   const groupsById = useMemo(() => {
      const map = new Map();

      for (const group of Array.isArray(availableGroups)
         ? availableGroups
         : []) {
         if (group?.id != null) {
            map.set(String(group.id), group);
         }
      }

      return map;
   }, [availableGroups]);

   const currentGroup =
      currentGroupId == null ? null : groupsById.get(String(currentGroupId));

   const currentGroupDisplay = useMemo(() => {
      if (currentGroupId == null || currentGroupId === "") return "Fără grupă";
      if (currentGroup?.name) return currentGroup.name;
      if (
         shouldLoadGroups &&
         (groupsStatus === "idle" || groupsStatus === "loading")
      ) {
         return "Se verifică...";
      }
      return "Grupă necunoscută";
   }, [currentGroup?.name, currentGroupId, groupsStatus, shouldLoadGroups]);

   const availableGroupsForSelect = useMemo(() => {
      const list = Array.isArray(availableGroups)
         ? availableGroups.filter((group) => group?.id != null)
         : [];

      if (currentGroupId == null) return list;
      if (list.some((group) => String(group.id) === String(currentGroupId))) {
         return list;
      }

      return [
         {
            id: currentGroupId,
            name: currentGroup?.name || "Grupă curentă",
         },
         ...list,
      ];
   }, [availableGroups, currentGroup?.name, currentGroupId]);

   const profileRows = useMemo(() => {
      const rows = [
         {
            key: "email",
            label: "Email",
            value: email || "—",
         },
         {
            key: "phone",
            label: "Telefon",
            value: phone || "—",
         },
      ];

      if (canShowSensitiveProfileFields) {
         rows.unshift({
            key: "group",
            label: "Grupa curentă",
            value: currentGroupDisplay,
         });
         rows.push({
            key: "idnp",
            label: "IDNP",
            value: idnp || "—",
         });
      }

      const canShowDesiredInstructor =
         Boolean(desiredInstructorId) &&
         (!maskOtherInstructors ||
            isViewerInstructorPerson(
               { id: desiredInstructorId },
               { instructor: viewerInstructor, user: viewerUser },
            ));
      if (canShowDesiredInstructor) {
         rows.push({
            key: "desiredInstructor",
            label: "Instructor preferat",
            value: desiredInstructorName || "—",
         });
      }

      if (canShowSensitiveProfileFields) {
         rows.push(
            {
               key: "medical_documents",
               label: "Doc. medicale",
               value: extrasForm.medical_documents ? "On" : "Off",
            },
            {
               key: "individual_work",
               label: "Lucru individual",
               value: extrasForm.individual_work ? "On" : "Off",
            },
            {
               key: "canBookPracticalExam",
               label: "Ex. practic",
               value: !extrasForm.__hasCanBookPracticalExam
                  ? "Necunoscut"
                  : extrasForm.canBookPracticalExam
                    ? "Permis"
                    : "Blocat",
            },
            {
               key: "number_of_absences",
               label: "Absențe",
               value: String(extrasForm.number_of_absences ?? 0),
            },
            {
               key: "privateMessage",
               label: "Notiță",
               value: noteValue.trim() || "—",
            },
         );
      }

      return rows;
   }, [
      canShowSensitiveProfileFields,
      currentGroupDisplay,
      desiredInstructorId,
      desiredInstructorName,
      maskOtherInstructors,
      viewerInstructor,
      viewerUser,
      email,
      phone,
      idnp,
      extrasForm.medical_documents,
      extrasForm.individual_work,
      extrasForm.canBookPracticalExam,
      extrasForm.__hasCanBookPracticalExam,
      extrasForm.number_of_absences,
      noteValue,
   ]);

   /* ✅ safe return */
   /* ===================== mesajele (bula de sus) ===================== */
   // erorile și confirmările din profil: AlertPills, nu sub câmpuri (filele: useProfileAlert)
   const [alerts, setAlerts] = useState([]);
   const pushAlert = useCallback((type, text) => {
      const message = String(text || "").trim();
      if (!message) return;
      setAlerts([{ id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, type, text: message }]);
   }, []);
   const popAlert = useCallback(() => setAlerts([]), []);
   useEffect(() => {
      if (instError) pushAlert("error", instError);
   }, [instError, pushAlert]);
   useEffect(() => {
      if (canShowAdminNote && noteError) pushAlert("error", noteError);
   }, [canShowAdminNote, noteError, pushAlert]);
   useEffect(() => {
      if (deleteAllReservationsError) pushAlert("error", deleteAllReservationsError);
   }, [deleteAllReservationsError, pushAlert]);

   if (!student) return null;

   const handleDownloadPdf = async (examId) => {
      setDownloadError("");
      setDownloadingId(examId);
      try {
         await downloadExamPdf(examId);
      } catch (e) {
         setDownloadError(e?.message || "Descărcarea a eșuat.");
      } finally {
         setDownloadingId(null);
      }
   };

   // Borderoul examenului practic: documentul elevului, completat de backend
   // din rezervarea de examen (nu din examenul teoretic).
   const handleDownloadBorderou = async (reservationId) => {
      setDownloadError("");
      setDownloadingDocumentId(reservationId);
      try {
         await downloadPracticalExamIndividualDocument(reservationId);
      } catch (e) {
         setDownloadError(e?.message || "Descărcarea a eșuat.");
      } finally {
         setDownloadingDocumentId(null);
      }
   };

   const studentInfoContextValue = {
      pushAlert,
      studentId,
      isStaffViewer,
      studentPolygonDistrict:
         liveStudent?.polygonDistrict ?? student?.polygonDistrict ?? null,
      onPolygonSaved: (polygonDistrict) =>
         setLiveStudent((p) => ({ ...p, polygonDistrict })),
      canEditStudentProfile,
      formData,
      handleChange,
      canShowSensitiveProfileFields,
      groupsStatus,
      availableGroupsForSelect,
      absText,
      extrasSaving,
      extrasReady,
      onAbsFocus,
      onAbsChange,
      onAbsBlur,
      onAbsKeyDown,
      desiredInstructorId,
      desiredInstructorName,
      extrasForm,
      toggleField,
      groupsError,
      extrasError,
      saveError,
      saving,
      canDeleteStudent,
      deleteError,
      deleting,
      handleDelete,
      handleSave,
      resetProfileForm,
      profileRows,
      loading,
      error,
      sortedReservations,
      setReservationsAscending,
      reservationsAscending,
      reservationNumberByKey,
      fromReservationId,
      currentReservationStart,
      canOpenReservationEdit,
      maskOtherInstructors,
      viewerInstructor,
      viewerUser,
      attemptsLoading,
      attemptsError,
      downloadError,
      attempts,
      handleDownloadPdf,
      downloadingId,
      handleDownloadBorderou,
      downloadingDocumentId,
      reviews,
      reviewsLoading,
      reviewsError,
      reviewsAscending,
      setReviewsAscending,
   };

   return (
      <StudentInfoContext.Provider value={studentInfoContextValue}>
      {/* bula de sus, pe pagină (nu în ramă, care o mută și o pune peste titlu): sus, la mijloc */}
      {typeof document !== "undefined"
         ? createPortal(<AlertPills messages={alerts} onDismiss={popAlert} />, document.body)
         : null}
      <div className="studentsProfileUI studentsProfileUI--popup ">
         {/* Header */}
         <div className="studentsProfileUI__header">
            <h3 className="studentsProfileUI__title">
               Profil elev
            </h3>

            {/* butoanele de lângă „Închide”: le pune rama popup-ului (PopupActions) */}
            <PopupActions>
               {canPurgeReservations ? (
                  <ButtonUI
                     variant="danger"
                     icon={cancelIcon}
                     title="Șterge toate rezervările"
                     aria-label="Șterge toate rezervările"
                     onClick={() => {
                        setPurgeOpen(true);
                        setPurgeChecked(false);
                        setDeleteAllReservationsError("");
                     }}
                  />
               ) : null}
            </PopupActions>
         </div>

         <div
            style={{ padding: 0 }}
            className="studentsProfileUI__content students-info__content students-info-popup"
         >
            {/* elevul: numele și telefonul pe un rând, dedesubt notița (un clic pe rând sau pe creion o
                deschide la scris, chiar în rând); în dreapta soldul, de aceeași înălțime */}
            <div className="studentsProfileUI__studentRow">
               <div
                  className={
                     "studentItem studentsProfileUI__studentItemTop studentsProfileUI__head" +
                     (canShowAdminNote ? " is-noteable" : "") +
                     (noteEditing ? " is-editing" : "") +
                     (noteSaving ? " is-saving" : "")
                  }
                  onClick={canShowAdminNote && !noteEditing ? () => setNoteEditing(true) : undefined}
                  role={canShowAdminNote && !noteEditing ? "button" : undefined}
                  tabIndex={canShowAdminNote && !noteEditing ? 0 : undefined}
                  onKeyDown={
                     canShowAdminNote && !noteEditing
                        ? (e) => {
                             if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setNoteEditing(true);
                             }
                          }
                        : undefined
                  }
                  title={canShowAdminNote && !noteEditing ? "Scrie notița" : undefined}
               >
                  {/* oaspetele: AvatarUI pune singur fantoma (person.isGuest); adminul și managerul
                      pun / schimbă / șterg poza elevului din avatar (meniul pozei, ca la personal) */}
                  {isStaffViewer && !isGuestStudent && headStudentId ? (
                     <ProfilePhotoAvatar
                        person={headPerson}
                        color={avatarBg}
                        initials={avatarInitials}
                        name={`${fn} ${ln}`.trim()}
                        photo={{
                           mode: "upload",
                           target: headStudentId,
                           hasPhoto: Boolean(ownAvatarUrl(headPerson)),
                           onChanged: (url) => {
                              rememberAvatarChange(headStudentId, url);
                              setPhotoOverride(url);
                           },
                        }}
                        onStatus={(kind, text) => pushAlert?.(kind === "error" ? "error" : "success", text)}
                     />
                  ) : (
                     <UserAvatar
                        person={headPerson}
                        color={avatarBg}
                        initials={avatarInitials}
                        className="studentItem__avatar"
                     />
                  )}
                  <div className="studentItem__info">
                     <h3 className="studentItem__name">
                        {`${fn} ${ln}`.trim() || "–"}
                        {phone ? <span className="studentsProfileUI__headPhone"> · {phone}</span> : null}
                        {showIdnpInHeaderMeta && idnp ? (
                           <span className="studentsProfileUI__headPhone"> · IDNP {idnp}</span>
                        ) : null}
                     </h3>
                     {canShowAdminNote ? (
                        noteEditing ? (
                           <input
                              className="studentsProfileUI__noteInput"
                              aria-label="Notiță"
                              value={noteValue}
                              onChange={onNoteChange}
                              onBlur={onNoteBlur}
                              onKeyDown={onNoteKeyDown}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Scrie o notiță…"
                              type="text"
                              autoComplete="off"
                              // eslint-disable-next-line jsx-a11y/no-autofocus
                              autoFocus
                           />
                        ) : (
                           <p className={"studentItem__meta studentsProfileUI__noteText" + (noteValue ? "" : " is-empty")}>
                              {noteValue || "Scrie o notiță…"}
                           </p>
                        )
                     ) : null}
                  </div>
                  {canShowAdminNote ? (
                     <button
                        type="button"
                        className="studentsProfileUI__noteBtn"
                        aria-label={noteEditing ? "Gata cu notița" : "Scrie notița"}
                        title={noteEditing ? "Gata (salvează)" : "Scrie notița"}
                        // mousedown: înainte de blur-ul câmpului, ca „gata” să nu-l redeschidă
                        onMouseDown={(e) => noteEditing && e.preventDefault()}
                        onClick={(e) => {
                           e.stopPropagation();
                           if (noteEditing) document.activeElement?.blur?.();
                           else setNoteEditing(true);
                        }}
                     >
                        <UIIcon name={noteEditing ? "check" : "edit"} className="studentsProfileUI__noteIcon" />
                     </button>
                  ) : null}
               </div>
               {canSeeFinanceTab && !isGuestStudent && balanceBani != null ? (
                  <button
                     type="button"
                     className={"studentsProfileUI__balance" + (balanceBani > 0 ? " is-positive" : "")}
                     title="Soldul din cont · deschide Finanțe"
                     onPointerDown={pressPoint}
                     onClick={() => setTab("finance")}
                  >
                     <span className="studentsProfileUI__balanceLabel">Sold</span>
                     {formatLei(balanceBani)}
                  </button>
               ) : null}
            </div>

            {/* „Mesaje” nu e o filă: deschide discuția cu acest elev într-un sub-popup, lângă profil */}
            <ProfileTabs
               items={profileTabs}
               value={tab}
               onChange={(key) => {
                  if (key !== "messages") {
                     setTab(key);
                     return;
                  }
                  openSubPopup("studentChat", {
                     component: StudentChatSubPopup,
                     student: { ...student, ...liveStudent, id: studentId, firstName: fn, lastName: ln, phone },
                  });
               }}
            />

            {tab === "profile" && <StudentOverviewTab />}
            {tab === "reservations" && <StudentReservationsTab />}
            {tab === "attempts" && <StudentExamsTab />}
            {tab === "reviews" && <StudentReviewsTab />}
            {tab === "finance" && canSeeFinanceTab && (
               <StudentFinanceTab
                  studentId={studentId}
                  canAdjust={hasAnyRole(viewerUser, [ROLES.ADMIN])}
                  isGuest={isGuestStudent}
               />
            )}
         </div>

         {purgeOpen ? (
            <div
               className="studentsProfileUI__purgeOverlay"
               role="dialog"
               aria-modal="true"
               aria-labelledby="studentsProfileUI-purge-title"
               onClick={(e) => {
                  if (e.target === e.currentTarget && !deletingAllReservations) {
                     resetPurgeUi();
                  }
               }}
            >
               <div className="studentsProfileUI__purgeModal">
                  <h4
                     id="studentsProfileUI-purge-title"
                     className="studentsProfileUI__purgeTitle"
                  >
                     Șterge rezervările
                  </h4>
                  <p className="studentsProfileUI__purgeHint">
                     Sigur vrei să ștergi toate rezervările acestui elev?
                  </p>
                  <label className="studentsProfileUI__purgeCheck">
                     <input
                        type="checkbox"
                        checked={purgeChecked}
                        onChange={(e) => setPurgeChecked(e.target.checked)}
                        disabled={deletingAllReservations}
                     />
                     <span>Da, sunt sigur că vreau să le șterg</span>
                  </label>
                  <div className="studentsProfileUI__purgeActions">
                     <button
                        type="button"
                        className="studentsProfileUI__purgeCancel"
                        onClick={resetPurgeUi}
                        disabled={deletingAllReservations}
                     >
                        Anulează
                     </button>
                     <button
                        type="button"
                        className="studentsProfileUI__purgeBtn"
                        onClick={onPurgeConfirmClick}
                        disabled={!purgeChecked || deletingAllReservations}
                     >
                        {deletingAllReservations
                           ? "Se șterg..."
                           : "Șterge definitiv"}
                     </button>
                  </div>
               </div>
            </div>
         ) : null}
      </div>
      </StudentInfoContext.Provider>
   );
}
